import Model, { attr, belongsTo, hasMany, type HasMany } from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import { createLocalizedProxy, type CustomTranslations } from 'invite-sign/utils/translations';

import type QuestionnaireFieldOption from './questionnaire-field-option';
import type QuestionnaireSection from './questionnaire-section';

export type QuestionKind = 'single-selection' | 'multiple-selection';

const TRANSLATABLE_ATTRIBUTE_MAPPING = {
  label: 'name',
  name: 'name',
  text: 'name',
} as const;
type TranslatableAttribute = keyof typeof TRANSLATABLE_ATTRIBUTE_MAPPING;

export default class QuestionnaireField extends Model {
  declare [Type]: 'questionnaire-field';

  @belongsTo('questionnaire-section', { async: false, inverse: 'questionnaireFields' })
  declare questionnaireSection: QuestionnaireSection;

  @hasMany('questionnaire-field-option', { async: false, inverse: 'questionnaireField' })
  declare questionnaireFieldOptions: HasMany<QuestionnaireFieldOption>;

  @attr() declare customTranslations: CustomTranslations<TranslatableAttribute>;

  @attr('string') declare kind: QuestionKind;
  @attr('string') declare label?: string;
  @attr('string') declare name?: string;
  @attr('number') declare position: number;

  /* Depending on which endpoint fetches `questionnaire-field` data, the question text will
   * _either_ be in a `label` attribute (`/api/v2/guest-invites/:id`) or `name` (`/api/v3/ipad-configs`).
   * This getter gives us a single property to read to get the text.
   */
  get text(): string {
    return this.label ?? this.name ?? '';
  }

  forLocale(locale: string, defaultLocale: string): this {
    if (locale !== defaultLocale) {
      return createLocalizedProxy(this, TRANSLATABLE_ATTRIBUTE_MAPPING, locale);
    }
    return this;
  }
}
